<!--
  Screenshot Annotator (dialog content)
  =====================================
  Opened via NgDialogAnimationService.open(ScreenshotAnnotatorComponent, ...).
  Caption and linked-measurement go through the `annotatorForm` reactive form.
-->
<div class="ss-annotator" [formGroup]="annotatorForm" @zoomIn>
  <!-- Header -->
  <header class="ss-annotator-header">
    <div class="ss-annotator-header-left">
      <input
        type="text"
        class="ss-annotator-caption-input"
        formControlName="caption"
        placeholder="Add a caption…"
        aria-label="Screenshot caption"
      />
      <span class="ss-annotator-page-info">{{ screenshot.pageName }}</span>
    </div>
    <div class="ss-annotator-header-actions">
      <button
        type="button"
        class="ss-annotator-btn ss-annotator-btn-success"
        (click)="requestShare()"
      >
        <mat-icon>share</mat-icon>
        <span>Share</span>
      </button>
      <button
        type="button"
        class="ss-annotator-btn ss-annotator-btn-dark"
        (click)="jumpToSource()"
      >
        <mat-icon>my_location</mat-icon>
        <span>Locate snapshot</span>
      </button>
      <button
        type="button"
        class="ss-annotator-btn ss-annotator-btn-icon"
        matTooltip="Close (Esc)"
        aria-label="Close annotator"
        (click)="close()"
      >
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </header>

  <!-- Drawing toolbar -->
  <div class="ss-annotator-toolbar" role="toolbar" aria-label="Annotation tools">
    <!-- Tool buttons -->
    <div class="ss-annotator-toolbar-group">
      <button
        type="button"
        class="ss-annotator-tool-btn"
        *ngFor="let t of tools"
        [class.ss-annotator-tool-btn-active]="tool === t.id"
        [matTooltip]="t.label + ' (' + t.shortcut.toUpperCase() + ')'"
        matTooltipPosition="below"
        (click)="setTool(t.id)"
      >
        <mat-icon>{{ t.icon }}</mat-icon>
      </button>
    </div>

    <div class="ss-annotator-toolbar-divider" aria-hidden="true"></div>

    <!-- Color swatches -->
    <div class="ss-annotator-toolbar-group ss-annotator-colors">
      <button
        type="button"
        class="ss-annotator-color"
        *ngFor="let c of colors"
        [style.background]="c"
        [class.ss-annotator-color-active]="activeColor === c"
        [matTooltip]="c"
        matTooltipPosition="below"
        (click)="setColor(c)"
        [attr.aria-label]="'Color ' + c"
      ></button>
    </div>

    <div class="ss-annotator-toolbar-divider" aria-hidden="true"></div>

    <!-- Edit actions -->
    <div class="ss-annotator-toolbar-group">
      <button
        type="button"
        class="ss-annotator-tool-btn"
        matTooltip="Undo (Ctrl+Z)"
        matTooltipPosition="below"
        (click)="undo()"
      >
        <mat-icon>undo</mat-icon>
      </button>
      <button
        type="button"
        class="ss-annotator-tool-btn"
        matTooltip="Delete last"
        matTooltipPosition="below"
        (click)="deleteLast()"
      >
        <mat-icon>backspace</mat-icon>
      </button>
      <button
        type="button"
        class="ss-annotator-tool-btn"
        matTooltip="Clear all"
        matTooltipPosition="below"
        (click)="clear()"
      >
        <mat-icon>delete_sweep</mat-icon>
      </button>
    </div>

    <div class="ss-annotator-toolbar-divider" aria-hidden="true"></div>

    <!-- Linked measurement dropdown -->
    <div class="ss-annotator-link-wrap">
      <mat-icon class="ss-annotator-link-icon" aria-hidden="true">link</mat-icon>
      <select
        class="ss-annotator-link-select"
        formControlName="linkedMeasurementId"
        aria-label="Link to measurement"
      >
        <option [ngValue]="null">No measurement linked</option>
        <option *ngFor="let m of linkableMeasurements" [ngValue]="m.id">{{ m.name }}</option>
      </select>
    </div>

    <!-- Spacer + SAVE -->
    <div class="ss-annotator-toolbar-spacer"></div>
    <button
      type="button"
      class="ss-annotator-btn ss-annotator-btn-primary"
      matTooltip="Save (Ctrl+S)"
      (click)="save()"
    >
      <mat-icon>check</mat-icon>
      <span>Save</span>
    </button>
  </div>

  <!-- Canvas area -->
  <div class="ss-annotator-canvas-area">
    <div
      class="ss-annotator-canvas"
      #canvasArea
      [style.background-image]="'url(' + screenshot.thumbnail + ')'"
      [style.min-width.px]="screenshot.region.width"
      [style.min-height.px]="screenshot.region.height"
    >
      <!-- SVG overlay for vector annotations -->
      <svg
        class="ss-annotator-svg"
        [attr.viewBox]="'0 0 ' + screenshot.region.width + ' ' + screenshot.region.height"
        preserveAspectRatio="xMidYMid meet"
        (mousedown)="onSvgMouseDown($event)"
        (mousemove)="onSvgMouseMove($event)"
        (mouseup)="onSvgMouseUp($event)"
        (mouseleave)="onSvgMouseUp($event)"
        [style.cursor]="tool === 'select' ? 'default' : 'crosshair'"
      >
        <ng-container *ngFor="let a of renderList; trackBy: trackByAnnotation">
          <!-- Rectangle -->
          <rect
            *ngIf="a.kind === 'rect'"
            [attr.x]="a.x"
            [attr.y]="a.y"
            [attr.width]="a.width"
            [attr.height]="a.height"
            fill="none"
            [attr.stroke]="a.color"
            [attr.stroke-width]="a.strokeWidth || 3"
          ></rect>
          <!-- Highlight (filled, semi-transparent) -->
          <rect
            *ngIf="a.kind === 'highlight'"
            [attr.x]="a.x"
            [attr.y]="a.y"
            [attr.width]="a.width"
            [attr.height]="a.height"
            [attr.fill]="a.color"
            fill-opacity="0.35"
            stroke="none"
          ></rect>
          <!-- Arrow shaft + head -->
          <ng-container *ngIf="a.kind === 'arrow'">
            <line
              [attr.x1]="a.x"
              [attr.y1]="a.y"
              [attr.x2]="a.endX"
              [attr.y2]="a.endY"
              [attr.stroke]="a.color"
              [attr.stroke-width]="a.strokeWidth || 3"
              stroke-linecap="round"
            ></line>
            <polygon
              [attr.points]="arrowHeadPath(a)"
              [attr.fill]="a.color"
            ></polygon>
          </ng-container>
          <!-- Pen freehand -->
          <path
            *ngIf="a.kind === 'pen'"
            [attr.d]="penPath(a)"
            fill="none"
            [attr.stroke]="a.color"
            [attr.stroke-width]="a.strokeWidth || 4"
            stroke-linejoin="round"
            stroke-linecap="round"
          ></path>
        </ng-container>
      </svg>

      <!-- DOM text-annotation overlays -->
      <input
        type="text"
        class="ss-annotator-text"
        *ngFor="let a of annotations; trackBy: trackByAnnotation"
        [hidden]="a.kind !== 'text'"
        [style.left.px]="a.x"
        [style.top.px]="a.y"
        [style.color]="a.color"
        [value]="a.text || ''"
        placeholder="Type…"
        (input)="onTextInputChange(a, $event)"
        (click)="$event.stopPropagation()"
        (mousedown)="$event.stopPropagation()"
      />
    </div>
  </div>
</div>
